/* eslint-disable react/prop-types */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Checkbox, message, notification, Space, Typography,
} from 'antd';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { getRetroactivoCandidatos } from 'api/nominas/calculosDeNominaEncabezado';
import RetroactivoAgregarModal from './RetroactivoAgregarModal';

const { Text } = Typography;

const formatFecha = (fecha) => {
  if (!fecha) return '';
  const [anio, mes, dia] = fecha.split('-');
  return `${dia}/${mes}/${anio}`;
};

// Tabla local (sin backend propio: los renglones solo existen en memoria hasta guardar la
// Nómina), así que el permiso es siempre completo -- no depende del modelo de permisos de
// Django como el resto de las tablas que usan este mismo componente compartido.
const PERMISO_COMPLETO = {
  model: 'retroactivo',
  add: true,
  change: true,
  delete: true,
  hasAllViewPermission: true,
};

/**
 * Contenido del tab "Retroactivos" de Agregar Nómina: tabla de empleados/periodos que se
 * van a pagar de forma retroactiva, inicialmente vacía. Usa el mismo componente de tabla
 * (`components/Table`) y el mismo patrón de selección múltiple + Agregar/Eliminar que el
 * resto de las pantallas del sistema (ver Asignación de Plazas) en vez de una tabla y
 * buscadores hechos a mano.
 *
 * El botón "Agregar" abre el selector de candidatos (RetroactivoAgregarModal); desde esta
 * tabla se edita el número de días a pagar de un renglón ya agregado, o se eliminan los
 * renglones seleccionados.
 */
const RetroactivoDetalleTab = ({
  tipoDeNominaEmpleado, periodoDeNominaDetalle, sujetoLabel, value, onChange, soloConsulta,
}) => {
  const [candidatos, setCandidatos] = useState([]);
  const [loadingCandidatos, setLoadingCandidatos] = useState(false);
  const [visibleAgregar, setVisibleAgregar] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [visibleDelete, setVisibleDelete] = useState(false);

  // Los candidatos dependen del Tipo de Nómina Empleado y del Periodo -- si cualquiera de
  // los dos cambia despues de haber agregado renglones, esa seleccion ya no es valida
  // (pudo cambiar que periodos aplican, los dias sugeridos, etc.) y se limpia con aviso.
  const filasSeleccionadasAntesDelCambio = useRef(value);
  filasSeleccionadasAntesDelCambio.current = value;
  const combinacionAnteriorRef = useRef(null);

  useEffect(() => {
    const combinacionActual = `${tipoDeNominaEmpleado || ''}-${periodoDeNominaDetalle || ''}`;
    const huboCambioPrevio = combinacionAnteriorRef.current !== null
      && combinacionAnteriorRef.current !== combinacionActual;
    combinacionAnteriorRef.current = combinacionActual;

    if (huboCambioPrevio && filasSeleccionadasAntesDelCambio.current.length) {
      onChange([]);
      setSelectedRowKeys([]);
      message.warning('Cambió el Tipo de Nómina Empleado o el Periodo: se reinició la selección de retroactivo.');
    }

    if (!tipoDeNominaEmpleado || !periodoDeNominaDetalle) {
      setCandidatos([]);
      return undefined;
    }
    let vigente = true;
    (async () => {
      setLoadingCandidatos(true);
      const data = await getRetroactivoCandidatos(tipoDeNominaEmpleado, periodoDeNominaDetalle);
      if (!vigente) return;
      setCandidatos(Array.isArray(data) ? data : []);
      setLoadingCandidatos(false);
    })();
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoDeNominaEmpleado, periodoDeNominaDetalle]);

  const filasYaAgregadas = useMemo(() => new Set(value.map((fila) => fila.key)), [value]);

  const resumen = useMemo(() => {
    const empleados = new Set(value.map((fila) => fila.empleadoId));
    const totalDias = value.reduce((acc, fila) => acc + (Number(fila.numeroDeDias) || 0), 0);
    return { empleados: empleados.size, periodos: value.length, totalDias };
  }, [value]);

  // Dataset efectivo (filtrado/visible) para "Seleccionar/Deseleccionar Todos" -- igual
  // patrón que ya usa Asignación de Plazas: nunca onSelectAll de AntD, que solo opera
  // sobre la página actual.
  const effectiveData = currentTableData.length ? currentTableData : value;

  const handleTableChange = (_pagination, _filters, _sorter, extra) => {
    if (extra?.currentDataSource) setCurrentTableData(extra.currentDataSource);
  };

  // El checkbox de cada renglón no es clicable directamente: regla global del sistema
  // (Layout/index.scss) -- "tbody .ant-table-cell.ant-table-selection-column label
  // { pointer-events: none }" -- para que un clic no dispare a la vez el onChange del
  // checkbox y el onClick de la fila. La selección se activa con el mismo mecanismo que
  // el resto de las tablas del sistema: handleOnRowClick (ver AdministracionDePlazas.jsx).
  const handleOnRowClick = (record) => {
    setSelectedRowKeys((prev) => (prev.includes(record.key)
      ? prev.filter((key) => key !== record.key)
      : [...prev, record.key]));
  };

  const onClickAdd = () => setVisibleAgregar(true);

  const onClickDelete = () => setVisibleDelete(true);

  // Agregar/Eliminar aqui solo modifican el estado en memoria (value/onChange) -- esta
  // tabla no tiene backend propio (ver PERMISO_COMPLETO arriba), asi que nada se persiste
  // ni se refleja en el calculo hasta que el usuario presiona "Guardar y Recalcular" en el
  // encabezado. Mismo patron de aviso que "Recálculo iniciado"/"Cálculo completado".
  const avisarCambiosSinGuardar = () => {
    notification.info({
      message: 'Cambios sin guardar',
      description: 'Este cambio en Retroactivos aún no se ha guardado. Haz clic en "Guardar y Recalcular" para aplicarlo al cálculo.',
      placement: 'topRight',
      duration: 0,
    });
  };

  const confirmarEliminar = () => {
    onChange(value.filter((fila) => !selectedRowKeys.includes(fila.key)));
    setSelectedRowKeys([]);
    setVisibleDelete(false);
    avisarCambiosSinGuardar();
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={effectiveData.length > 0 && selectedRowKeys.length === effectiveData.length}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < effectiveData.length}
        onChange={(e) => {
          setSelectedRowKeys(e.target.checked ? effectiveData.map((fila) => fila.key) : []);
        }}
      />
    ),
    onSelect: (record, selected) => {
      setSelectedRowKeys((prev) => (selected
        ? [...prev, record.key]
        : prev.filter((key) => key !== record.key)));
    },
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const columnas = [
    {
      titleText: sujetoLabel || 'Empleado',
      dataIndex: 'nombreEmpleado',
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plazaClaveYDescripcion',
    },
    {
      titleText: 'Fecha de Asignación',
      dataIndex: 'fechaAsignacion',
      render: (valor) => formatFecha(valor),
    },
    {
      titleText: 'Periodo',
      dataIndex: 'numeroDeNomina',
      render: (valor, fila) => (
        `${fila.numeroDeNomina} (Del ${formatFecha(fila.fechaInicial)} al ${formatFecha(fila.fechaFinal)})`
      ),
    },
    {
      titleText: 'Días del Periodo',
      dataIndex: 'numeroDeDiasPeriodo',
      width: 110,
    },
    {
      // Solo lectura: numeroDeDias ya viene calculado automaticamente (descuenta
      // incidencias/ausentismo no laborado, ver retroactivo_candidatos.py) -- no es un
      // valor que el usuario deba poder modificar a mano.
      titleText: 'Días a Pagar',
      dataIndex: 'numeroDeDias',
      width: 130,
    },
  ];

  return (
    <div>
      <Table
        rowKey="key"
        cols={columnas}
        data={value}
        count={value.length}
        titleTable="Retroactivos"
        permission={PERMISO_COMPLETO}
        rowSelection={rowSelection}
        handleOnRowClick={handleOnRowClick}
        controls={soloConsulta ? {} : { onClickAdd, onClickDelete }}
        allowAdd
        allowDelete
        allowEdit={false}
        allowUnasign={false}
        allowMultiSelect={!soloConsulta}
        allowMultiDeselect={!soloConsulta}
        onMultiSelect={() => setSelectedRowKeys(effectiveData.map((fila) => fila.key))}
        onMultiDeselect={() => setSelectedRowKeys([])}
        onTableChange={handleTableChange}
        disableAdd={!tipoDeNominaEmpleado || !periodoDeNominaDetalle || loadingCandidatos}
        disableDelete={!selectedRowKeys.length}
        loading={loadingCandidatos}
      />
      {!!value.length && (
        <Space style={{ marginTop: 10 }}>
          <Text strong>
            {resumen.empleados}
            {' '}
            {sujetoLabel || 'Empleado'}
            {resumen.empleados === 1 ? '' : 's'}
            {' · '}
            {resumen.periodos}
            {' Periodo'}
            {resumen.periodos === 1 ? '' : 's'}
            {' · '}
            {resumen.totalDias}
            {' Día'}
            {resumen.totalDias === 1 ? '' : 's'}
            {' en Total'}
          </Text>
        </Space>
      )}

      <ModalDelete
        open={visibleDelete}
        onCancel={() => setVisibleDelete(false)}
        onDelete={confirmarEliminar}
        content={(
          <Text style={{ fontSize: 16 }}>
            {selectedRowKeys.length === 1
              ? '¿Eliminar el renglón de retroactivo seleccionado?'
              : `¿Eliminar los ${selectedRowKeys.length} renglones de retroactivo seleccionados?`}
          </Text>
        )}
      />

      <RetroactivoAgregarModal
        open={visibleAgregar}
        onCancel={() => setVisibleAgregar(false)}
        candidatos={candidatos}
        filasYaAgregadas={filasYaAgregadas}
        sujetoLabel={sujetoLabel}
        onConfirm={(filasElegidas) => {
          onChange([...value, ...filasElegidas]);
          setVisibleAgregar(false);
          avisarCambiosSinGuardar();
        }}
      />
    </div>
  );
};

export default RetroactivoDetalleTab;
